import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/**
 * SPEC-GAP: §6 lists Button, Chip, Card, FactRow, ParticipantChip,
 * ComplianceNotice, AnnouncementCard, MessageBubble and EmptyState — no form
 * field. Per §10 ("prefer the nearest specified pattern"), these are built from
 * existing tokens only: --text-label for the label exactly as FactRow uses it,
 * --color-border-strong for the border as buttons use it, --radius-md, and the
 * 48px control height buttons already carry. No new colour, size or radius.
 */

interface FieldShellProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /**
   * This control filters what is already on screen rather than collecting a value to submit, so
   * the "(optional)" note is suppressed (TROOP-178).
   *
   * That note answers *must I fill this in before submitting?*, and a box with nothing to submit
   * has no answer to give — "SEARCH THE ROSTER (optional)" reads as a form field somebody may skip
   * rather than as the filter it is. Not the same thing as `required`, which would also put a
   * constraint on the input that nothing enforces.
   */
  search?: boolean;
  children: ReactNode;
}

function FieldShell({ label, htmlFor, hint, error, required, search, children }: FieldShellProps) {
  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
      <label htmlFor={htmlFor} className="t-label" style={{ color: 'var(--color-text-muted)' }}>
        {label}
        {/* A word, not an asterisk: an asterisk means nothing until you find its legend. */}
        {!required && !search && (
          <span style={{ textTransform: 'none', letterSpacing: 0 }}> (optional)</span>
        )}
      </label>

      {children}

      {hint && !error && (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {hint}
        </p>
      )}

      {error && (
        <p
          className="t-caption"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: 0 }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A boolean, with the explanation beside it rather than in a hint underneath.
 *
 * **Not FieldShell.** That shell puts a `--text-label` caption above its control and appends the
 * literal word "(optional)" to anything not required — both wrong here. A checkbox's label belongs
 * beside the box, and "(optional)" on a thing whose whole nature is optional reads as a mistake.
 *
 * The markup is the pattern the announcement form already used for "pin it to the top": an 18px
 * box, a `--text-body-strong` line naming what it does, and a `--text-caption` line saying what it
 * means. Extracted so a second copy cannot drift from the first, which is the whole reason
 * `Field.tsx` exists at all.
 *
 * The whole row is the label, so the caption is part of the tap target rather than dead space
 * beside it.
 */
export function CheckboxField({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  /** One line on what turning it off actually does. Optional, but usually earns its place. */
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className="flex items-start gap-3"
      style={{ cursor: disabled ? 'default' : 'pointer', minHeight: 'var(--tap-min)' }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
        style={{ marginTop: '0.2em', width: 18, height: 18, flexShrink: 0 }}
      />
      <span>
        <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
          {label}
        </span>
        {hint && (
          <span
            className="t-caption"
            style={{ display: 'block', color: 'var(--color-text-secondary)' }}
          >
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

const controlStyle: React.CSSProperties = {
  minHeight: '48px',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--color-border-strong)',
  background: 'var(--color-surface-raised)',
  color: 'var(--color-text-primary)',
  font: 'var(--text-body)',
  width: '100%',
};

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  /** Filters what is on screen rather than collecting a value. See {@link FieldShellProps.search}. */
  search?: boolean;
  /**
   * React 19 passes `ref` as an ordinary prop, so it rides `...rest` onto the input with no
   * `forwardRef` wrapper. Declared only because `InputHTMLAttributes` does not carry it.
   */
  ref?: Ref<HTMLInputElement>;
}

export function TextField({ label, hint, error, required, search, ...rest }: TextFieldProps) {
  const id = useId();

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      hint={hint}
      error={error}
      required={required}
      search={search}
    >
      <input
        id={id}
        {...rest}
        required={required}
        aria-invalid={error ? true : undefined}
        style={controlStyle}
      />
    </FieldShell>
  );
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function SelectField({ label, hint, error, required, children, ...rest }: SelectFieldProps) {
  const id = useId();

  return (
    <FieldShell label={label} htmlFor={id} hint={hint} error={error} required={required}>
      <select id={id} {...rest} required={required} style={controlStyle}>
        {children}
      </select>
    </FieldShell>
  );
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  /** Rides `...rest` onto the textarea, as `TextField`'s does — the curated-text toolbar needs the selection. */
  ref?: Ref<HTMLTextAreaElement>;
}

/**
 * Same shell, same border, same radius as TextField — only the height differs, because the
 * content does. Per §10 this reuses the specified control rather than inventing a second one.
 */
export function TextAreaField({ label, hint, error, required, rows = 4, ...rest }: TextAreaFieldProps) {
  const id = useId();

  return (
    <FieldShell label={label} htmlFor={id} hint={hint} error={error} required={required}>
      <textarea
        id={id}
        rows={rows}
        {...rest}
        required={required}
        aria-invalid={error ? true : undefined}
        style={{ ...controlStyle, minHeight: 'unset', padding: 'var(--space-3)', resize: 'vertical' }}
      />
    </FieldShell>
  );
}
